/**
 * i18next initialization.
 *
 * Translation dictionaries are NOT bundled. They live in public/locales/<code>/
 * and are fetched at runtime by i18next-http-backend, which means a new
 * language — or a correction to an existing one — ships by copying JSON files
 * to the server. No rebuild, no redeploy of the app bundle.
 *
 * Language *selection* is owned by LanguageContext, not by this module and not
 * by i18next's own detector: the choice has to weigh a per-retailer default and
 * a stored visitor override, which the detector knows nothing about. This file
 * only configures the machinery; the context drives it.
 */

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import {
  BUILTIN_LOCALES,
  DEFAULT_NAMESPACE,
  NAMESPACES,
  SOURCE_LOCALE,
  resolveChain,
  type LocaleDef,
} from '../config/locales';
import { BUILD_NUMBER } from '../version';

/**
 * Dictionaries are served from the app origin. In local dev the React server
 * and the PHP API live on different hosts, but public/ is served by CRA itself,
 * so this stays origin-relative and needs no API_URL.
 *
 * The build number rides along because the server lets these files be cached
 * for a few minutes: without it, a browser that fetched a dictionary just
 * before a deploy pairs the new bundle with the old strings, and every key
 * added in that build renders raw (e.g. "share.modal.title").
 */
const LOAD_PATH = `/locales/{{lng}}/{{ns}}.json?v=${BUILD_NUMBER}`;

/** Same base the rest of the app uses for API calls. */
const API_URL = process.env.REACT_APP_API_URL || '';

let initPromise: Promise<void> | null = null;

/**
 * The chain *after* the active locale — what i18next should try when a key is
 * missing. Never empty: an empty array disables fallback entirely, which would
 * surface raw keys instead of English.
 */
function fallbackFor(locale: string, registry: LocaleDef[]): string[] {
  const rest = resolveChain(locale, registry).slice(1);
  return rest.length > 0 ? rest : [SOURCE_LOCALE];
}

/**
 * Initialize i18next once. Safe to call repeatedly — later calls get the same
 * promise, so a remount or a Fast Refresh cannot double-init.
 *
 * @param locale   the locale to start in
 * @param registry the active registry, used to build the fallback chain
 */
export function initI18n(locale: string, registry: LocaleDef[] = BUILTIN_LOCALES): Promise<void> {
  if (initPromise) return initPromise;

  initPromise = i18next
    .use(HttpBackend)
    .use(initReactI18next)
    .init({
      lng: locale,

      // Every chain ends at English, so a key missing from a partial
      // translation renders the source string instead of a raw key. Starting
      // in English leaves nothing after the slice, hence the guard — i18next
      // treats an empty array as "no fallback at all".
      fallbackLng: fallbackFor(locale, registry),

      // supportedLngs is deliberately NOT set, for two reasons:
      //
      //   1. i18next snapshots it into languageUtils at init, so mutating
      //      i18next.options.supportedLngs later has no effect. The live
      //      registry arrives from /api/locales.php *after* init, so any
      //      language added via the DB but absent from BUILTIN_LOCALES would
      //      be permanently unloadable — which is exactly the "add a language
      //      without a rebuild" promise this design is built on.
      //   2. nonExplicitSupportedLngs (needed to make supportedLngs tolerant of
      //      regional tags) matches with the region stripped, so a request for
      //      fr-CA resolves to plain "fr", finds no dictionary, and silently
      //      drops to English.
      //
      // Our own registry already decides which locales are offered, so there is
      // nothing for i18next to gate. It just loads what LanguageContext asks
      // for, and the fallback chain handles anything missing.

      ns: [...NAMESPACES],
      defaultNS: DEFAULT_NAMESPACE,
      // Only fetch the exact locale; the fallback chain above handles the rest.
      load: 'currentOnly',

      backend: { loadPath: LOAD_PATH },

      interpolation: {
        // React escapes for us; double-escaping would render literal &amp;.
        escapeValue: false,
      },

      react: {
        // Suspense off on purpose. The app already has one gate that holds
        // paint until boot data is ready (ProductsBootGate), and LanguageProvider
        // awaits init the same way — one controlled loading state beats scattering
        // Suspense boundaries around every component that calls useTranslation.
        useSuspense: false,
      },

      // In dev, surface untranslated keys in the console — this is how the
      // Phase 2 extraction sweep finds the strings it missed.
      debug: false,
      saveMissing: process.env.NODE_ENV === 'development',
      missingKeyHandler: (lngs, ns, key) => {
        if (process.env.NODE_ENV !== 'development') return;
        console.warn(`[i18n] missing key: ${ns}:${key} (${lngs.join(', ')})`);
      },
    })
    // init() resolves with a TFunction; callers only care that it finished.
    .then(() => applyUiOverrides(locale));

  return initPromise;
}

/**
 * Switch language at runtime and re-point the fallback chain at the new locale.
 *
 * Changing language alone would leave a stale chain behind (switching es-MX →
 * fr-CA would keep falling back through Spanish), so the chain is rewritten
 * first. Unlike supportedLngs, fallbackLng *is* read live from options on each
 * resolve, so mutating it here takes effect.
 */
export async function changeLocale(locale: string, registry: LocaleDef[]): Promise<void> {
  i18next.options.fallbackLng = fallbackFor(locale, registry);
  await i18next.changeLanguage(locale);
  await applyUiOverrides(locale);
}

/**
 * Merge database-held UI string overrides over the shipped dictionaries.
 *
 * The dictionaries in public/locales are the baseline the build ships. Anything
 * a translator sent back is IMPORTED into the database, because files are
 * replaced wholesale by deploy.sh and an import written into them would vanish
 * on the next release. This pulls those overrides in and layers them on top.
 *
 * Failure is silent on purpose: no overrides is the normal state (English, a
 * fresh locale, an offline visitor), and the shipped files are already a
 * complete, correct dictionary. Blocking boot on this would trade a working
 * page for a slightly fresher one.
 */
export async function applyUiOverrides(locale: string): Promise<void> {
  if (!locale || locale === SOURCE_LOCALE) return;
  try {
    const res = await fetch(
      `${API_URL}/api/ui-strings.php?locale=${encodeURIComponent(locale)}`,
      { signal: AbortSignal.timeout(4000) },
    );
    if (!res.ok) return;
    const data = await res.json();
    const strings = data?.strings;
    if (!strings || typeof strings !== 'object') return;

    for (const [ns, bundle] of Object.entries(strings as Record<string, unknown>)) {
      if (!bundle || typeof bundle !== 'object') continue;
      // deep = true so a partial override merges into the namespace rather than
      // replacing it; overwrite = true so it actually wins over the shipped file.
      i18next.addResourceBundle(locale, ns, bundle, true, true);
    }
  } catch {
    /* offline / pre-migrate — the shipped dictionaries stand on their own */
  }
}

export default i18next;
